import type {
  BillingCadence,
  QuotationExportSnapshot,
  QuotationLineItem,
} from "../model";
import { formatMoney, formatQuantity } from "../money";
import { calculateLineAmountMinor } from "../totals";

const RESERVED_FILE_NAMES = new Set([
  "CON",
  "PRN",
  "AUX",
  "NUL",
  "COM1",
  "COM2",
  "COM3",
  "COM4",
  "COM5",
  "COM6",
  "COM7",
  "COM8",
  "COM9",
  "LPT1",
  "LPT2",
  "LPT3",
  "LPT4",
  "LPT5",
  "LPT6",
  "LPT7",
  "LPT8",
  "LPT9",
]);

export function sanitizeFileNamePart(value: string): string {
  const withoutControlCharacters = Array.from(value)
    .filter((character) => character.charCodeAt(0) >= 32)
    .join("");
  const normalized = withoutControlCharacters
    .replace(/[<>:"/\\|?*]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/[.\s]+$/g, "")
    .trim()
    .slice(0, 64);

  if (normalized.length === 0) {
    return "Customer";
  }

  return RESERVED_FILE_NAMES.has(normalized.toUpperCase())
    ? `_${normalized}`
    : normalized;
}

export function createQuotationFileName(
  snapshot: QuotationExportSnapshot,
  extension: "pdf" | "docx",
): string {
  const customer = sanitizeFileNamePart(snapshot.quotation.customer.name);
  return `Quotation_${customer}_${snapshot.quotation.issueDate}.${extension}`;
}

export function lineItemsForCadence(
  snapshot: QuotationExportSnapshot,
  cadence: BillingCadence,
): QuotationLineItem[] {
  return snapshot.quotation.lineItems.filter(
    (lineItem) => lineItem.billingCadence === cadence,
  );
}

export function lineAmountLabel(
  snapshot: QuotationExportSnapshot,
  lineItem: QuotationLineItem,
): string {
  if (lineItem.optional && !lineItem.included) {
    return "Excluded option";
  }

  const amount = calculateLineAmountMinor(lineItem);
  return amount === null
    ? "TBD"
    : formatMoney(
        amount,
        snapshot.quotation.currency,
        snapshot.quotation.currencyMinorUnits,
      );
}

export function lineUnitPriceLabel(
  snapshot: QuotationExportSnapshot,
  lineItem: QuotationLineItem,
): string {
  return lineItem.unitPriceMinor === null || lineItem.valueStatus === "tbd"
    ? "TBD"
    : formatMoney(
        lineItem.unitPriceMinor,
        snapshot.quotation.currency,
        snapshot.quotation.currencyMinorUnits,
      );
}

export function lineQuantityLabel(lineItem: QuotationLineItem): string {
  return `${formatQuantity(lineItem.quantityMilli)} ${lineItem.unit}`.trim();
}
